import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import client, { apiErrorMessage, auth as authStore } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { CardSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/lib/format';

export default function ProfilePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => client.get('/auth/me').then((r) => r.data.data),
  });

  const [form, setForm] = useState({ fullName: '', mobile: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (q.data) setForm({ fullName: q.data.fullName || '', mobile: q.data.mobile || '' });
  }, [q.data]);

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await client.patch('/auth/me', form);
      // Update stored user
      const u = authStore.getUser();
      authStore.set({ user: { ...u, fullName: res.data.data.fullName } });
      toast.success('Profile updated');
      qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  }

  const [cp, setCp] = useState({ current: '', next: '', confirm: '' });
  const [changing, setChanging] = useState(false);

  async function changePassword(e) {
    e.preventDefault();
    if (cp.next !== cp.confirm) return toast.error('Passwords do not match');
    setChanging(true);
    try {
      await client.post('/auth/change-password', { currentPassword: cp.current, newPassword: cp.next });
      toast.success('Password changed. Please sign in again.');
      authStore.clear();
      navigate('/admin/login', { replace: true });
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setChanging(false); }
  }

  return (
    <div data-testid="profile-page">
      <PageHeader title="Profile & security" description="Update your name, contact, and password." />
      {q.isLoading ? <CardSkeleton height={200} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={saveProfile} className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="profile-form">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-4">Personal details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Full name</Label>
                  <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="h-10" data-testid="profile-fullname" />
                </div>
                <div>
                  <Label className="text-xs">Mobile</Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-10 font-mono" data-testid="profile-mobile" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Email</Label>
                  <Input value={q.data?.email || ''} disabled className="h-10 font-mono bg-zinc-50" />
                  <div className="text-[11px] text-zinc-500 mt-1">Email cannot be changed. Contact platform admin if needed.</div>
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <Button type="submit" disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="profile-save-button">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
                </Button>
              </div>
            </form>

            <form onSubmit={changePassword} className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="change-password-form">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-4">Change password</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Current password</Label>
                  <Input type="password" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} className="h-10" data-testid="cp-current" required />
                </div>
                <div>
                  <Label className="text-xs">New password</Label>
                  <Input type="password" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })} className="h-10" data-testid="cp-next" required minLength={8} />
                </div>
                <div>
                  <Label className="text-xs">Confirm new</Label>
                  <Input type="password" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} className="h-10" data-testid="cp-confirm" required minLength={8} />
                </div>
              </div>
              <div className="text-xs text-zinc-500 mt-3">Must have upper, lower, and a digit. Min 8 chars. All sessions will be revoked.</div>
              <div className="mt-5 flex justify-end">
                <Button type="submit" disabled={changing} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="cp-submit">
                  {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" /> Change password</>}
                </Button>
              </div>
            </form>
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg p-6 h-fit">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">Account</div>
            <div className="space-y-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Role</div><div className="font-mono">{q.data?.role}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Last login</div><div className="font-mono text-xs">{q.data?.lastLoginAt ? formatDate(q.data.lastLoginAt) : '—'}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Created</div><div className="font-mono text-xs">{formatDate(q.data?.createdAt)}</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
